<script setup lang="ts">
import {
  PhMagnifyingGlass as MagnifyingGlassIcon,
  PhSlidersHorizontal as SlidersHorizontalIcon,
} from '@phosphor-icons/vue'
import { computed, onMounted, ref } from 'vue'
import ProductCard from '../components/ProductCard.vue'
import StatusState from '../components/StatusState.vue'
import { api } from '../services/api'
import type { Product } from '../types'

const products = ref<Product[]>([])
const loading = ref(true)
const error = ref('')
const search = ref('')
const category = ref('全部')
const secondaryCategory = ref('全部')
const sortMode = ref<'default' | 'price-asc' | 'price-desc'>('default')

const categories = computed(() => ['全部', ...new Set(products.value.map((item) => item.category))])
const secondaryCategories = computed(() => {
  if (category.value === '全部') return []
  return [
    '全部',
    ...new Set(
      products.value
        .filter((item) => item.category === category.value)
        .map((item) => item.secondaryCategory)
        .filter(Boolean),
    ),
  ]
})

function searchableProductText(product: Product) {
  const colorNames = Array.isArray(product.colors)
    ? product.colors.map((color) => color?.name)
    : []
  const productParameters = Array.isArray(product.parameterGroups)
    ? product.parameterGroups.flatMap((group) =>
        Array.isArray(group?.items)
          ? group.items.flatMap((item) => [item?.label, item?.value])
          : [],
      )
    : []
  const skuParameters = Array.isArray(product.skus)
    ? product.skus.flatMap((sku) =>
        Array.isArray(sku?.parameters)
          ? sku.parameters.flatMap((item) => [item?.label, item?.value])
          : [],
      )
    : []

  return [
    product.name,
    product.summary,
    product.secondaryCategory,
    ...colorNames,
    ...productParameters,
    ...skuParameters,
  ]
    .map((value) => String(value ?? '').toLocaleLowerCase('zh-CN'))
    .join('\n')
}

const filteredProducts = computed(() => {
  const keyword = search.value.trim().toLocaleLowerCase('zh-CN')
  const filtered = products.value.filter((product) => {
    const categoryMatches = category.value === '全部' || product.category === category.value
    const secondaryCategoryMatches =
      secondaryCategory.value === '全部' || product.secondaryCategory === secondaryCategory.value
    const keywordMatches = !keyword || searchableProductText(product).includes(keyword)
    return categoryMatches && secondaryCategoryMatches && keywordMatches
  })
  if (sortMode.value === 'default') return filtered
  return [...filtered].sort((left, right) => {
    const leftPrice = left.basePriceYuan != null && left.basePriceYuan > 0
      ? left.basePriceYuan
      : null
    const rightPrice = right.basePriceYuan != null && right.basePriceYuan > 0
      ? right.basePriceYuan
      : null
    if (leftPrice == null && rightPrice == null) return left.sortOrder - right.sortOrder
    if (leftPrice == null) return 1
    if (rightPrice == null) return -1
    const priceDifference = sortMode.value === 'price-asc'
      ? leftPrice - rightPrice
      : rightPrice - leftPrice
    return priceDifference || left.sortOrder - right.sortOrder
  })
})

function selectCategory(value: string) {
  category.value = value
  secondaryCategory.value = '全部'
}

async function loadProducts() {
  loading.value = true
  error.value = ''
  try {
    products.value = await api.listProducts()
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : '产品加载失败'
  } finally {
    loading.value = false
  }
}

onMounted(loadProducts)
</script>

<template>
  <section class="page-hero page-hero--catalog">
    <div class="container page-hero__inner">
      <div>
        <h1>找到适合你家空间的柜款</h1>
        <p>选好颜色、尺寸和五金，提交询价，等专业量房上门。</p>
      </div>
    </div>
  </section>

  <section class="section section--catalog">
    <div class="container">
      <div class="catalog-toolbar">
        <label class="search-field">
          <span class="sr-only">搜索产品</span>
          <MagnifyingGlassIcon :size="20" aria-hidden="true" />
          <input v-model="search" type="search" placeholder="搜索柜款、颜色或参数" />
        </label>
        <div class="category-filter" aria-label="按品类筛选">
          <SlidersHorizontalIcon :size="19" aria-hidden="true" />
          <button
            v-for="item in categories"
            :key="item"
            type="button"
            :class="{ active: category === item }"
            @click="selectCategory(item)"
          >
            {{ item }}
          </button>
        </div>
        <label class="catalog-sort">
          <span>排序</span>
          <select v-model="sortMode" aria-label="商品排序">
            <option value="default">默认排序</option>
            <option value="price-asc">价格从低到高</option>
            <option value="price-desc">价格从高到低</option>
          </select>
        </label>
      </div>
      <div
        v-if="secondaryCategories.length > 1"
        class="category-filter category-filter--secondary"
        aria-label="按细分类型筛选"
      >
        <span>细分类型</span>
        <button
          v-for="item in secondaryCategories"
          :key="item"
          type="button"
          :class="{ active: secondaryCategory === item }"
          @click="secondaryCategory = item"
        >
          {{ item }}
        </button>
      </div>

      <StatusState v-if="loading" kind="loading" />
      <StatusState
        v-else-if="error"
        kind="error"
        title="产品暂时无法显示"
        :message="error"
        @retry="loadProducts"
      />
      <StatusState
        v-else-if="filteredProducts.length === 0"
        kind="empty"
        title="没有匹配的柜款"
        message="试试其他关键词或品类。"
      >
        <button
          class="button button--secondary"
          type="button"
          @click="search = ''; category = '全部'; secondaryCategory = '全部'"
        >
          清除筛选
        </button>
      </StatusState>
      <div v-else class="product-grid product-grid--catalog">
        <ProductCard v-for="product in filteredProducts" :key="product.id" :product="product" />
      </div>
    </div>
  </section>
</template>
